import { useParams } from "react-router-dom";
import { useEffect, useState, ChangeEvent, FormEvent } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Error from "./404";
import ReCaptchaCheckbox from "../utils/ReCaptchaCheckbox";
import { postComment } from "../services/api";
import { loadBlogDetailBySlug } from "../services/contentSource";
import Seo, { articleSchema } from "../components/Seo";

  const BlogDetails = () => {
    const [token, setToken] = useState<string | null>(null);
    const [blog, setBlog] = useState<any>(null);
    const [missing, setMissing] = useState(false);
    const { slug } = useParams<{ slug: string }>();
    const blogId: string | undefined = blog?.id;

    const [formData, setFormData] = useState({
      blogId: "",
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    });

    const [showSuccess, setShowSuccess] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);


    useEffect(() => {
      if (!slug) {
        setMissing(true);
        return;
      }
      let cancelled = false;

      loadBlogDetailBySlug(slug)
        .then((match) => {
          if (cancelled) return;
          if (!match) {
            setMissing(true);
            return;
          }
          setBlog(match);
          setFormData((prev) => ({ ...prev, blogId: match.id }));
        })
        .catch(() => {
          if (!cancelled) setMissing(true);
        });

      return () => {
        cancelled = true;
      };
    }, [slug]);

    if (missing) {
      return <Error />;
    }

    if (!blog) {
      return null;
    }

    const handleChange = (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name.toLowerCase()]: value }));
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Checked before the submitting flag is set: returning early after
      // setIsSubmitting(true) left the button disabled until a page reload.
      if (!token) {
        toast.error("Please complete the reCAPTCHA check.");
        return;
      }

      setIsSubmitting(true);

      try {
        const data = await postComment({ ...formData, token: token || "" });

        if (data.success) {
          setShowSuccess(true);
          setIsSubmitting(false);
          setTimeout(() => {
            setShowSuccess(false);
            setFormData({
              blogId: blogId || "", // keep blogId after reset
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: "",
            });
          }, 3000);
        } else {
          toast.error("Failed to send message. Please try again.");
          setIsSubmitting(false);
        }
      } catch (error) {
        toast.error("Something went wrong. Please try again later.");
        setIsSubmitting(false);
        console.error(error);
      }
    };

    if (!blog) {
      return <p>Loading blog details...</p>;
    }

    return (
      <>
      <Seo
        title={String(blog.title ?? "Blog")}
        description={String(blog.paragraphs?.[0] ?? blog.title ?? "Engineering notes from the MindSyncX team.").replace(/<[^>]*>/g, " ").slice(0, 200)}
        path={`/blogs/${slug}`}
        image={blog.mainImage}
        type="article"
        structuredData={articleSchema({
          headline: String(blog.title ?? ""),
          description: String(blog.paragraphs?.[0] ?? "").replace(/<[^>]*>/g, " ").slice(0, 200),
          image: blog.mainImage,
          datePublished: blog.date,
          path: `/blogs/${slug}`,
        })}
      />
        <link rel="stylesheet" href="/assets/css/master-blog-details.css" />
        <main>
          <section className="blog-details-area">
            <div className="container">
              <div className="blog-details-area-inner">
                <div className="section-header">
                  <div className="section-title-wrapper">
                    <div className="title-wrapper">
                      <h1 className="section-title large has_fade_anim">
                        {blog.title}
                      </h1>
                    </div>
                  </div>
                  <div className="meta-box has_fade_anim">
                    <ul>
                      <li>
                        <span className="number">{blog.comments}</span>
                        <p className="text">Comments</p>
                      </li>
                      <li>
                        <span className="number">{blog.likes}</span>
                        <p className="text">Likes</p>
                      </li>
                      <li>
                        <span className="number">{(blog.date || "").split(" ")[0] || ""}</span>
                        <p className="text">{(blog.date || "").split(" ").slice(1).join(" ") || blog.date || ""}</p>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="blog-thumb overflow-hidden">
                  <img
                    className="w-100"
                    data-speed="0.8"
                    src={blog.mainImage}
                    alt="image" loading="lazy" decoding="async" />
                </div>

                <div className="blogdetails__wrapper">
                  <div className="blogdetails-contentleft">
                    <ul
                      className="blogdetails-overview dark-overview has_fade_anim"
                      data-fade-from="left"
                    >
                      <li>
                        <i className="fa-solid fa-chart-simple" />
                        <span>
                          {blog.views}
                          <br />
                          Views
                        </span>
                      </li>
                      {/* <li>
                        <i className="fa-solid fa-share-nodes" />
                        <span>
                          {blog.shares}
                          <br />
                          Shares
                        </span>
                      </li> */}
                      <li>
                        <a
                          href="https://www.youtube.com/@MindSyncX"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fa-brands fa-youtube"></i>
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.facebook.com/profile.php?id=61577047823875&sk=about"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fa-brands fa-facebook-f"></i>
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.x.com/mindsyncx"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          𝕏
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.instagram.com/mindsyncx_labs"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fa-brands fa-instagram"></i>
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.linkedin.com/company/mindsyncxlabs"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fa-brands fa-linkedin"></i>
                        </a>
                      </li>
                    </ul>
                  </div>

                  <div className="blogdetails-contentright">
                    <article className="blog-details-fullBody">
                      <div className="blog-details-quote has_fade_anim">
                        <div className="shape-1">
                          <img
                            src="/assets/imgs/icon/quote-11.webp"
                            alt="quote" loading="lazy" decoding="async" />
                        </div>
                        <div className="icon">
                          <img
                            src="/assets/imgs/icon/quote-10.webp"
                            alt="quote" loading="lazy" decoding="async" />
                        </div>
                        <div className="blog-details-quote-info">
                          <div className="blog-details-quote-text">
                            <p className="text">{blog.quote.text}</p>
                          </div>
                          <div className="blog-details-quote-author">
                            <span className="name has-left-line">
                              {blog.quote.author}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="text-wrapper">
                        {blog.paragraphs.map((text: any, index: any) => (
                          <p key={index} className="text has_fade_anim">
                            {text}
                          </p>
                        ))}
                      </div>

                      {blog.sections.map((section: any, index: any) => (
                        <div key={index} className="content-block">
                          <h2 className="content-title has_fade_anim">
                            {section.title}
                          </h2>
                          <div className="text-wrapper">
                            <p className="text has_fade_anim">
                              {section.content}
                            </p>
                          </div>
                          {/* {section.image && (
                            <div className="thumb overflow-hidden has_fade_anim">
                              <img
                                className="w-100"
                                data-speed="0.8"
                                src={section.image}
                                alt="image" loading="lazy" decoding="async" />
                            </div>
                          )} */}
                          {section.title === "WorkFlow" && (
                            <div className="details-service has_fade_anim">
                              <ul>
                                {blog.services.map((service: any, index: any) => (
                                  <li key={index}>
                                    <span>+</span>
                                    {service}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}

                      <div className="tagswrap has_fade_anim">
                        <ul className="tags">
                          <li>
                            <span>Tags:</span>
                          </li>
                          {blog.tags.map((tag: any, index: any) => (
                            <li key={index}>
                              <a href="#">{tag}</a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </article>

                    <div className="commentform section-spacing-top has_fade_anim">
                      <h2 className="section-title has_fade_anim">
                        Write a comment
                      </h2>
                      <div className="contact-wrap">
                        <form onSubmit={handleSubmit}>
                          <div className="contact-formwrap">
                            <div className="contact-formfield">
                              <input
                                type="text"
                                name="name"
                                id="Name"
                                placeholder="Name*"
                                value={formData.name}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="contact-formfield">
                              <input
                                type="email"
                                name="email"
                                id="Email"
                                placeholder="Email*"
                                value={formData.email}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="contact-formfield">
                              <input
                                type="tel"
                                name="phone"
                                id="Phone"
                                placeholder="Phone"
                                value={formData.phone}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="contact-formfield">
                              <input
                                type="text"
                                name="subject"
                                id="Subject"
                                placeholder="Subject*"
                                value={formData.subject}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="contact-formfield message">
                              <textarea
                                name="message"
                                id="Messages"
                                placeholder="Messages*"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                className="text-area"
                              />
                            </div>
                          </div>

                          <div className="submit-btn">
                            <ReCaptchaCheckbox onChange={setToken} />
                            <button
                              type="submit"
                              className="wc-btn wc-btn-primary btn-text-flip"
                              disabled={isSubmitting}
                            >
                              <span data-text="Send Message">
                                {isSubmitting ? "Submitting..." : "Send Message"}
                              </span>
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="blog-area">
            <div className="container">
              <div className="blog-area-inner section-spacing">
                {blog.relatedPosts.length > 0 && (
                  <div className="section-content">
                    <div className="section-title-wrapper">
                      <div className="title-wrapper">
                        <h2 className="section-title has_fade_anim">
                          Related articles
                        </h2>
                      </div>
                    </div>
                  </div>
                )}
                {blog.relatedPosts.length > 0 && (
                  <div className="blogs-wrapper-box">
                    <div className="blogs-wrapper has_fade_anim">
                      {blog.relatedPosts.map((item: any) => (
                        <a key={item.id} href={item.link}>
                          <div className="blog-box">
                            <div className="thumb">
                              <img src={item.img} alt="image" loading="lazy" decoding="async" />
                            </div>
                            <div className="content">
                              <span className="number">{item.id}</span>
                              <h3 className="title">{item.title}</h3>
                              <span className="icon">
                                <i className="fa-solid fa-arrow-right" />
                              </span>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </>
    );
  };

  export default BlogDetails;
